import React from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import { useFocusEffect } from 'expo-router'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import type { GameResponse } from '@warcarp/api-types'
import { teamsConfigOf } from '@warcarp/api-types'
import { PlayerColorChip } from '@warcarp/ui'
import { playerColorSlot } from '../lib/playerColor'
import { deriveWinner, deriveWinningTeamForGame } from '../lib/winner'
import { teamLabel } from '@warcarp/api-types'
import { currentPlayer as resolveCurrentPlayer } from '../lib/turnHelpers'
import { getItem } from '../lib/storage'
import { gameRowHasUnread, chatSeenKey } from '../lib/chatHelpers'
import { useTheme } from '../hooks/useTheme'
import { useMaps } from '../hooks/useMaps'
import { MapThumbnail } from './map/MapThumbnail'
import { getBundledMap } from '../lib/bundledMaps'
import { turnWaitingLabel } from '../lib/turnTimes'
import { useNow } from '../hooks/useNow'
import { teamsLobbyLabel } from '../lib/teamSetup'

const STATUS_BG: Record<string, string> = {
  ABANDONED: 'bg-blood',
  STARTING: 'bg-yellow-900',
  PAUSED: 'bg-amber-900',
}

// Exceptional states retain a light label against their dark status surface.
const STATUS_TEXT: Record<string, string> = {
  ABANDONED: 'text-white',
  STARTING: 'text-white',
  PAUSED: 'text-white',
}

export interface GameRowProps {
  game: GameResponse
  currentUserId: string | undefined
  onPress: () => void
  /** Static board preview shared by home and browse/join tiles. */
  showMapPreview?: boolean
  density?: 'normal' | 'compact'
  /** Read-only QA samples do not have a real chat history. */
  trackUnread?: boolean
  /** QA galleries use bundled maps without requiring a login or network. */
  loadMapFromNetwork?: boolean
}

// React.memo: GameRow's props are primitives + a stable GameResponse object.
// Combined with the useCallback-wrapped renderItem in the home FlatList this
// prevents row reconciliation when unrelated parent state changes.
/** Line height of the turn row's text; the player dot box matches it to stay on the first line. */
const TURN_LINE_HEIGHT = 20

/** Width of the your-turn outline (`border-2`), taken out of that card's padding. */
const YOUR_TURN_BORDER = 2

export const GameRow = React.memo(function GameRow({ game, currentUserId, onPress, showMapPreview = true, density = 'normal', trackUnread = true, loadMapFromNetwork = true }: GameRowProps) {
  const { data: maps } = useMaps({ enabled: loadMapFromNetwork })
  const mapName = maps?.find(map => map.id === game.mapId)?.name ?? getBundledMap(game.mapId ?? undefined)?.name ?? game.mapId ?? 'Default map'
  const me = game.players.find((p) => p.userId === currentUserId)
  // After resigning a player is added to state.eliminated. The engine
  // advances turnIndex past them but the GameResponse's scalar
  // currentPlayerIndex can briefly point at the resigner before the
  // state delta lands — and even after it lands, until the next turn
  // advance commits the new scalar. Either way the resigner is NOT on
  // turn — gate the badge on (a) being the current player AND (b) not
  // eliminated.
  const eliminated =
    (game.state as { eliminated?: string[] } | null)?.eliminated ?? []
  // Derive the displayed current player. currentPlayerIndex is a playerIndex
  // VALUE, not an array index (see resolveCurrentPlayer) — resolve by field and
  // skip eliminated/resigned slots so the tile reads the live player's name.
  const currentPlayer = React.useMemo(
    () => resolveCurrentPlayer(game, eliminated),
    [game, eliminated],
  )
  const isYourTurn =
    game.status === 'ACTIVE' &&
    !!me &&
    currentPlayer?.userId === currentUserId &&
    !eliminated.includes(String(me.slot))

  // For COMPLETE games, derive the winner from the engine's game-end
  // event in state.events (the only place the winner slot lives once
  // the game is done). Falls back to the last non-eliminated player
  // if for some reason no game-end event is present (stalled finishes
  // that still mark COMPLETE).
  const isComplete = game.status === 'COMPLETE'
  const winner = React.useMemo(() => deriveWinner(game), [game])
  // Team games: the winning team (every member wins).
  const winningTeam = React.useMemo(() => deriveWinningTeamForGame(game), [game])

  const statusBg = STATUS_BG[game.status] ?? 'bg-paper-up'
  const statusText = STATUS_TEXT[game.status] ?? 'text-ink'
  const isLobby = game.status === 'LOBBY'
  const lobbyTeams = isLobby ? teamsConfigOf(game.setupConfig) : undefined
  // Ordinary states are clear from the turn, seats or winner line. Retain
  // exceptional states so a paused/abandoned campaign is still identifiable.
  const showStatus = !['ACTIVE', 'LOBBY', 'COMPLETE'].includes(game.status)
  const now = useNow(game.status === 'ACTIVE' && !isYourTurn && game.turnStartedAt ? 60_000 : null)
  const waitingTime = turnWaitingLabel(game.turnStartedAt, now)

  // Unread-chat indicator (active + completed games only). Public-chat unread is
  // a client-local seen-id heuristic, so we read the per-game seen id from
  // storage and compare it to the server's latest message id; secret unread is
  // server-authoritative (game.unreadSecretCount). Re-read on focus so returning
  // from the chat/thread screen (which updates the seen id) clears the icon.
  const trackChat = trackUnread && (game.status === 'ACTIVE' || game.status === 'COMPLETE')
  const [seenChatId, setSeenChatId] = React.useState<string | null | undefined>(undefined)
  useFocusEffect(
    React.useCallback(() => {
      if (!trackChat) return
      let cancelled = false
      void getItem(chatSeenKey(game.id)).then((v) => {
        if (!cancelled) setSeenChatId(v ?? null)
      })
      return () => {
        cancelled = true
      }
    }, [game.id, trackChat]),
  )
  const showUnread =
    trackChat &&
    gameRowHasUnread({
      unreadSecretCount: game.unreadSecretCount,
      lastChatMessageId: game.lastChatMessageId,
      seenChatId,
    })
  const { theme } = useTheme()
  const inkColor = theme === 'dark' ? '#e6e1c4' : '#18170f'
  // Viewer has been invited but hasn't joined yet. Surfaced by the
  // /games (my-games) endpoint; null for endpoints that don't pass
  // viewerUserId to formatGame.
  const isPendingInvite = game.viewerInvitationStatus === 'pending'

  // Unread icon placement: float it at the RIGHT END of the card's body row
  // (the turn line on active games, the winner line on completed ones) so it
  // never adds an extra row of height. Only when the card has no body row at
  // all (pending invite, or a completed game with no derivable winner) does
  // it fall back to its own bottom row — the case that used to make an
  // absolutely-positioned icon collide with the COMPLETE pill.
  const showTurnRow = !isPendingInvite && game.status === 'ACTIVE' && !!currentPlayer
  const showWinnerRow = !isPendingInvite && isComplete && (!!winner || !!winningTeam)
  const unreadInBodyRow = showUnread && (showTurnRow || showWinnerRow)
  const unreadStandalone = showUnread && !unreadInBodyRow

  // Rendered directly as a row child (never wrapped) so its ml-auto always
  // reaches the row: a wrapper left it beside a short "Your turn!" label.
  // alignSelf centres it on a two-line turn row.
  const unreadIcon = (
    <View
      className="flex-row justify-end ml-auto"
      style={{ flexShrink: 0, alignSelf: 'center' }}
      accessibilityElementsHidden
      importantForAccessibility="no"
    >
      <View className="pr-1">
        <MaterialCommunityIcons name="message-text" size={22} color={inkColor} />
        <View
          pointerEvents="none"
          className="bg-blood rounded-full border border-paper"
          style={{ position: 'absolute', top: -4, right: -4, width: 12, height: 12 }}
        />
      </View>
    </View>
  )

  return (
    <TouchableOpacity
      onPress={onPress}
      // Outline the whole card when it's the viewer's turn — a call-to-action
      // that reads at a glance without parsing the "Your turn!" line. The
      // 2px outline comes out of the padding, so the content (and the
      // right-pinned unread icon) lines up with the cards that have none.
      className={`bg-paper rounded-xl p-4 gap-2 ${isYourTurn ? 'border-2 border-blood' : ''}`}
      accessibilityLabel={`Game: ${game.name}, status ${game.status}, map ${mapName}${isYourTurn ? ', your turn' : ''}${showUnread ? ', unread messages' : ''}`}
      accessibilityRole="button"
      style={{ minHeight: 44, padding: (density === 'compact' ? 12 : 16) - (isYourTurn ? YOUR_TURN_BORDER : 0) }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
      {showMapPreview && <MapThumbnail mapId={game.mapId} loadFromNetwork={loadMapFromNetwork} width={density === 'compact' ? 72 : 88} height={density === 'compact' ? 54 : 64} />}
      <View style={{ flex: 1, minWidth: 0, gap: density === 'compact' ? 5 : 8 }}>
      <View className="flex-row justify-between items-center gap-2">
        <Text className="text-ink font-stencil text-base flex-1 uppercase" numberOfLines={showMapPreview ? 2 : 1}>
          {game.name}
        </Text>
        {showStatus && <View className={`px-2 py-0.5 rounded-full ${statusBg}`}>
          <Text className={`text-xs font-stencil ${statusText} uppercase`}>
            {game.status}
          </Text>
        </View>}
      </View>

      {/* Player count only matters while filling seats — drop it once a game
          has started (active/complete/etc. show turn/winner info instead). */}
      {isLobby && (
        <View className="flex-row items-center gap-2">
          <Text className="text-ink-soft text-sm font-typed">
            {game.players.length}/{game.maxPlayers} players joined{lobbyTeams ? ` · ${teamsLobbyLabel(lobbyTeams)}` : ''}
          </Text>
        </View>
      )}

      {/* Pending invite: mirror the "Your turn!" treatment for visual
          parity — a blood-coloured stencil call-to-action under the
          status row. Takes precedence over the per-phase turn line
          since a pending invite means the viewer isn't a player. */}
      {isPendingInvite && (
        <Text className="text-blood text-sm font-stencil uppercase">
          Pending Invite!
        </Text>
      )}

      {/* Completed game: surface the winner with the same chip + stencil
          callout treatment the active "Your turn!" line gets, so every
          finished lobby card clearly reads who won (🏆 + handle). The unread
          chat icon floats at the right end of this same row. */}
      {showWinnerRow && (
        // Team games name the winning team with every winning member's chip.
        <View
          className="flex-row items-center gap-2"
          testID={winningTeam ? 'game-row-team-winner' : undefined}
          accessibilityLabel={winningTeam ? `${teamLabel(winningTeam.team)} won: ${winningTeam.members.map((m) => m.displayName).join(', ')}` : undefined}
        >
          {winningTeam ? (
            <View className="flex-row items-center gap-1">
              {winningTeam.members.map((member) => <PlayerColorChip key={member.slot} slot={playerColorSlot(member)} size="sm" />)}
            </View>
          ) : winner ? (
            <PlayerColorChip slot={playerColorSlot(winner)} size="sm" />
          ) : null}
          <Text className="text-ink text-sm font-stencil uppercase" style={{ flexShrink: 1 }}>
            {winningTeam ? teamLabel(winningTeam.team) : winner?.displayName} 🏆
          </Text>
          {unreadInBodyRow && unreadIcon}
        </View>
      )}

      {showTurnRow && currentPlayer && (
        <View className="flex-row items-start gap-2">
          {/* One text line tall, so the dot always centres on the player's
              name, never on a wrapped two-line block. */}
          <View testID="game-row-turn-dot" style={{ height: TURN_LINE_HEIGHT, justifyContent: 'center' }}>
            <PlayerColorChip slot={playerColorSlot(currentPlayer)} size="sm" />
          </View>
          {isYourTurn ? (
            <Text className="text-blood text-sm font-stencil uppercase" style={{ lineHeight: TURN_LINE_HEIGHT }}>Your turn!</Text>
          ) : (
            // Name and duration are separate pieces in a wrapping row: when
            // they don't fit on one line the whole "for …" phrase drops to the
            // next line instead of breaking mid-phrase.
            <View testID="game-row-turn-text" style={{ flex: 1, minWidth: 0, flexDirection: 'row', flexWrap: 'wrap', columnGap: 4 }}>
              <Text className="text-ink text-sm font-typed font-bold" style={{ lineHeight: TURN_LINE_HEIGHT }}>{currentPlayer.displayName}&apos;s turn</Text>
              {waitingTime ? (
                <Text className="text-ink-soft text-sm font-typed" style={{ lineHeight: TURN_LINE_HEIGHT }}>for {waitingTime}</Text>
              ) : null}
            </View>
          )}
          {unreadInBodyRow && unreadIcon}
        </View>
      )}

      {/* Unread icon fallback: only for cards with NO body row (pending
          invite, completed with no derivable winner). A flow row can't
          collide with the status pill the way the old absolutely-positioned
          icon did. */}
      {unreadStandalone && (
        <View className="flex-row">{unreadIcon}</View>
      )}
      </View>
      </View>
    </TouchableOpacity>
  )
})

export function GameSkeletonRow() {
  return (
    <View
      className="bg-paper rounded-xl p-4 gap-2"
      accessibilityLabel="Loading game"
    >
      <View className="flex-row items-center">
        <View className="h-4 w-40 bg-paper-up rounded" />
      </View>
      <View className="h-3 w-28 bg-paper-up rounded" />
    </View>
  )
}
